import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    memo,
} from "react";
import * as queue from "./offlineQueue";
import { syncQueue } from "./syncEngine";
import SyncToast from "./SyncToast";
import { getEnvVar } from "../config.common";

/* ── env-level kill switch ──────────────────────────────────────────── */
const ENV_OFFLINE_FIRST = getEnvVar("OFFLINE_FIRST_ENABLED", "true") === "true";
const normalizeFlag = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase() === "true";

/* ── Context ─────────────────────────────────────────────────────────── */

const OfflineCtx = createContext({
    isOnline: true,
    isServerReachable: true,
    offlineFirstEnabled: false,
    /** Enqueue a request to be synced later. Returns true if queued. */
    enqueueRequest: () => false,
});

export const useOffline = () => useContext(OfflineCtx);

/* ── Provider ────────────────────────────────────────────────────────── */

/**
 * OfflineProvider wraps the app tree and exposes:
 *   • isOnline            – live connectivity flag
 *   • offlineFirstEnabled – org-config feature gate
 *   • enqueueRequest(url, payload) – saves to queue when offline
 *
 * It also owns the SyncToast and triggers serial sync on reconnect.
 *
 * Props:
 *   orgSettings  {Array}  – template?.org?.settings from server
 *   userId       {string} – current logged-in user id
 */
const OfflineProvider = memo(function OfflineProvider({
    orgSettings,
    userId,
    children,
}) {
    /* ── online / offline ────────────────────────────────────── */
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isServerReachable, setIsServerReachable] = useState(true);
    const mountedRef = useRef(true);
    const hideTimerRef = useRef(null);
    useEffect(() => {
        const on = () => {
            // Mark online but do NOT recover reachability yet.
            // runSync will confirm the server is actually reachable.
            setIsOnline(true);
        };
        const off = () => setIsOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, [])

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, []);

    /* ── feature gate ────────────────────────────────────────── */
    const offlineFirstEnabled =
        ENV_OFFLINE_FIRST &&
        Array.isArray(orgSettings) &&
        normalizeFlag(
            orgSettings.find((s) => s.key === "useOfflineFirst")?.value,
        );

    /* ── sync toast state ────────────────────────────────────── */
    const [syncState, setSyncState] = useState({
        visible: false,
        processed: 0,
        skipped: 0,
        total: 0,
        done: false,
        error: null,
    });
    const syncingRef = useRef(false);

    /* ── sync on reconnect ───────────────────────────────────── */
    const scheduleHide = useCallback((delay) => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            setSyncState((s) => ({ ...s, visible: false }));
            hideTimerRef.current = null;
        }, delay);
    }, []);

    const markServerReachable = useCallback((reachable) => {
        if (mountedRef.current) setIsServerReachable(reachable);
    }, []);

    const runSync = useCallback(async () => {
        if (!userId || syncingRef.current) return;
        syncingRef.current = true;
        try {
            const pending = await queue.count(userId);
            if (pending === 0) {
                // Nothing queued — no API call to prove reachability against.
                // NOTE: this is an intentional trade-off, not an oversight —
                // an alternative ("don't recover without proof") was tried
                // and reverted because it gets stuck red forever after any
                // brief connectivity blip during which nothing happened to
                // be queued (the common case for an idle kiosk), which is a
                // worse failure mode than the rare false-positive green this
                // causes (browser online + API specifically down + nothing
                // queued, all at once). A real fix needs a cheap product-
                // agnostic health-check endpoint from the backend; without
                // one, trusting navigator.onLine is the least-bad option.
                markServerReachable(true);
                return;
            }

            // Queue is non-empty, but everything in it may still be inside
            // its transient-failure backoff window (queue.isDue() would
            // skip all of them). Don't run a pass in that case — it would
            // show a misleading "Synced 0 feedback" toast and a momentary
            // green dot every 30s during a real sustained outage, with zero
            // actual evidence gained. Silently wait for the next cycle.
            const dueCount = await queue.countDue(userId);
            if (dueCount === 0) return;

            if (mountedRef.current) {
                setSyncState({
                    visible: true,
                    processed: 0,
                    skipped: 0,
                    total: pending,
                    done: false,
                    error: null,
                });
            }

            await syncQueue(userId, (p) => {
                if (!mountedRef.current) return;
                // If we previously scheduled an auto-hide (from an earlier
                // error) but sync is still reporting live progress, cancel
                // it so the toast doesn't disappear mid-sync.
                if (hideTimerRef.current && !p.done && !p.error) {
                    clearTimeout(hideTimerRef.current);
                    hideTimerRef.current = null;
                }
                if (p.error) markServerReachable(false);
                else if (p.done || p.processed > 0) markServerReachable(true);

                setSyncState(() => ({
                    visible: true,
                    processed: p.processed,
                    skipped: p.skipped ?? 0,
                    total: p.total,
                    done: p.done ?? false,
                    error: p.error,
                }));
                if (p.done || p.error) {
                    // On error: dot stays red — only a successful next sync cycle
                    // (triggered by the next online event) recovers it.
                    // Check p.error first: a terminal report can be BOTH
                    // done and error (a batch where every item failed
                    // transiently with no successes) — that's still a
                    // failure and should get the longer error-hide delay,
                    // not the quick success delay.
                    scheduleHide(p.error ? 4000 : 1500);
                }
            });
        } catch (error) {
            if (mountedRef.current) {
                markServerReachable(false);
                setSyncState({
                    visible: true,
                    processed: 0,
                    skipped: 0,
                    total: 0,
                    done: false,
                    error: error?.message || "Offline sync failed unexpectedly",
                });
                scheduleHide(4000);
            }
        } finally {
            syncingRef.current = false;
        }
    }, [userId, scheduleHide, markServerReachable]);

    useEffect(() => {
        if (isOnline && offlineFirstEnabled) runSync();
    }, [isOnline, offlineFirstEnabled, runSync]);

    useEffect(() => {
        if (!offlineFirstEnabled) {
            // Feature off: reachability simply mirrors navigator.onLine.
            markServerReachable(isOnline);
            return;
        }
        if (!isOnline) {
            // Went offline — immediately mark unreachable.
            markServerReachable(false);
        }
        // When offlineFirstEnabled + online: deliberately do NOT auto-recover
        // here. runSync() is the confirmation step; it sets reachable=true on
        // success and reachable=false on failure.
    }, [isOnline, offlineFirstEnabled, markServerReachable]);

    /* ── Recovery loop: poll every 30 s while online + feature-enabled,
     *    NOT only while marked unreachable. This must NOT be gated on
     *    isServerReachable: a sync pass can end with items still queued but
     *    deferred by transient-failure backoff (queue.isDue() skips them,
     *    see syncEngine.js), which reports no evidence either way and
     *    leaves isServerReachable=true. If this loop only ran while
     *    unreachable, it would shut itself off and those deferred items
     *    would never get retried until a browser online/offline toggle.
     *    runSync() is a cheap no-op when the queue is empty or nothing is
     *    due yet, so polling unconditionally here is safe.        ─────── */
    useEffect(() => {
        if (!offlineFirstEnabled || !isOnline) return;
        const id = setInterval(() => {
            if (!syncingRef.current) runSync();
        }, 30_000);
        return () => clearInterval(id);
    }, [offlineFirstEnabled, isOnline, runSync]);

    /* ── enqueue helper ──────────────────────────────────────── */
    const enqueueRequest = useCallback(
        /**
         * @param {object} [options]
         * @param {boolean} [options.force] — bypass the isOnline/isServerReachable
         *   gate. Use when the CALLER just observed direct proof of a network
         *   failure (e.g. a request threw with no response) that hasn't been
         *   reflected into context state yet — without this, the very first
         *   failure that reveals an outage would be silently dropped instead
         *   of queued, since isServerReachable is still stale/true at that
         *   point (it only updates via browser online/offline events or a
         *   completed sync pass, neither of which has happened yet).
         */
        async (url, payload, meta = {}, options = {}) => {
            const { force = false } = options;
            if (!offlineFirstEnabled || !userId) return false;
            if (!force && isOnline && isServerReachable) return false; // caller should use normal axios
            if (force) markServerReachable(false); // this failure IS the evidence
            await queue.enqueue(userId, url, payload, meta);
            return true;
        },
        [offlineFirstEnabled, userId, isOnline, isServerReachable, markServerReachable],
    );

    /* ── value (stable ref via callbacks) ────────────────────── */
    const value = useMemo(
        () => ({
            isOnline,
            isServerReachable,
            offlineFirstEnabled,
            enqueueRequest,
        }),
        [isOnline, isServerReachable, offlineFirstEnabled, enqueueRequest],
    );

    return (
        <OfflineCtx.Provider value={value}>
            {children}
            <SyncToast {...syncState} />
            {/* Connectivity status dot — only relevant where offline-first
             * is actually in play (FBS tablet kiosk). Gated on the org-level
             * feature flag so other products (e.g. Checklists) mounted under
             * the same LoggedInUserShell don't get unexpected new UI. */}
            {offlineFirstEnabled && (
                <div
                    className="conn-status-dot"
                    title={
                        !isOnline
                            ? "Offline"
                            : isServerReachable
                              ? "Online"
                              : "Server unreachable"
                    }
                    style={{
                        background: !isOnline
                            ? "#da3633"
                            : isServerReachable
                              ? "#2ea043"
                              : "#d29922",
                    }}
                />
            )}
        </OfflineCtx.Provider>
    );
});

export default OfflineProvider;
